import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Stack,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Box,
  Chip,
  Typography,
} from '@mui/material';
import { CloudUpload as UploadIcon } from '@mui/icons-material';
import { DocumentListItem } from '../../../../../types';

interface EditDialogProps {
  open: boolean;
  onClose: () => void;
  selectedFile: File | null;
  fileName: string;
  setFileName: (name: string) => void;
  handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleEdit: () => void;
  uploading: boolean;
  selectedPages: string[];
  setSelectedPages: (pages: string[]) => void;
  availablePages: any[];
  selectedDocument: DocumentListItem | null;
}

const EditDialog: React.FC<EditDialogProps> = ({
  open,
  onClose,
  selectedFile,
  fileName,
  setFileName,
  handleFileSelect,
  handleEdit,
  uploading,
  selectedPages,
  setSelectedPages,
  availablePages,
  selectedDocument,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Document</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            label="File Name"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            required
          />
          
          <FormControl fullWidth>
            <InputLabel>Share with Pages</InputLabel>
            <Select
              multiple
              value={selectedPages}
              onChange={(event) => {
                const value = event.target.value;
                setSelectedPages(Array.isArray(value) ? value : []);
              }}
              input={<OutlinedInput label="Share with Pages" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {Array.isArray(selected) ? selected.map((value: string) => {
                    const permission = availablePages.find(p => p.permissionName === value);
                    return (
                      <Chip key={value} label={permission?.displayName || value} size="small" />
                    );
                  }) : null}
                </Box>
              )}
            >
              {availablePages.map((permission) => (
                <MenuItem key={permission.permissionId} value={permission.permissionName}>
                  <Box>
                    <Typography variant="body2">{permission.displayName}</Typography>
                    {permission.description && (
                      <Typography variant="caption" color="text.secondary">
                        {permission.description}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadIcon />}
            fullWidth
          >
            Replace File (Optional)
            <input
              type="file"
              accept=".pdf,.docx"
              hidden
              onChange={handleFileSelect}
            />
          </Button>
          
          {selectedFile && (
            <Alert severity="info">
              New file: {selectedFile.name}
            </Alert>
          )}
          
          {uploading && <LinearProgress />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={uploading}>
          Cancel
        </Button>
        <Button 
          onClick={handleEdit} 
          variant="contained" 
          disabled={uploading}
        >
          {uploading ? 'Updating...' : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditDialog;
